import type { GetQuickFilterTextParams } from 'ag-grid-community';
import type { Security } from '../../../types/Security';
import { ProductTypeEnum, type ProductTypeEnum as ProductType } from '../../../types/types';
import { resolveShowDescription } from '../../AgGrid/AgGridSecurity';
import type { AgGridSecurityDecorationParams, AgGridSecurityProps } from '../../AgGrid/types';
import { defaultGetQuickFilterText } from '../utils';

/**
 * Product types whose {@link Security.Description} should appear under the symbol in blotter
 * security/asset cells, quick-filter text, and formatted/exported asset values when shown.
 *
 * Add new {@link ProductTypeEnum} members here to extend eligibility app-wide.
 */
export const PRODUCT_TYPES_WITH_BLOTTER_SECURITY_DESCRIPTION: ReadonlySet<ProductType> = new Set([
  ProductTypeEnum.Share,
  ProductTypeEnum.GovernmentBond,
  ProductTypeEnum.Future,
]);

export function shouldShowBlotterSecurityDescription(productType: ProductType | undefined): boolean {
  return productType != null && PRODUCT_TYPES_WITH_BLOTTER_SECURITY_DESCRIPTION.has(productType);
}

export function getBlotterSecurityDescriptionSearchText(security: Security | undefined): string {
  if (!security?.Description) {
    return '';
  }

  const trimmed = security.Description.trim();
  return trimmed === '' ? '' : trimmed;
}

function createDecorationParams(
  context: GetQuickFilterTextParams['context'],
  data: unknown,
  symbol: string
): AgGridSecurityDecorationParams {
  return {
    data,
    security: context.current.securitiesBySymbol?.get(symbol),
    context,
  };
}

export function resolveSecurityColumnShowDescription(
  params: AgGridSecurityProps | undefined
): AgGridSecurityProps['showDescription'] {
  if (params?.showDescription !== undefined) {
    return params.showDescription;
  }

  if (params?.useBlotterSecurityDescriptionPolicy === false) {
    return false;
  }

  return ({ security }) => shouldShowBlotterSecurityDescription(security?.ProductType);
}

export function shouldIncludeSecurityDescriptionInSearch(
  showDescription: AgGridSecurityProps['showDescription'],
  decorationParams: AgGridSecurityDecorationParams
): boolean {
  if (showDescription === undefined) {
    return shouldShowBlotterSecurityDescription(decorationParams.security?.ProductType);
  }

  return resolveShowDescription(showDescription, decorationParams) === true;
}

export function appendBlotterSecurityDescriptionToFormattedValue(
  formattedValue: string,
  security: Security | undefined,
  showDescription: AgGridSecurityProps['showDescription'],
  decorationParams: AgGridSecurityDecorationParams
): string {
  if (!shouldIncludeSecurityDescriptionInSearch(showDescription, decorationParams)) {
    return formattedValue;
  }

  const descriptionText = getBlotterSecurityDescriptionSearchText(security);
  if (descriptionText === '') {
    return formattedValue;
  }

  return formattedValue === '' ? descriptionText : `${formattedValue} ${descriptionText}`;
}

export function getSecurityColumnDescriptionQuickFilterText(
  params: GetQuickFilterTextParams,
  showDescription: AgGridSecurityProps['showDescription'],
  existingText = ''
): string {
  const value = params.value;
  if (value == null || value === '') {
    return '';
  }

  // Description search must mirror the scalar security renderer that can display it.
  if (typeof value !== 'string') {
    return '';
  }

  const decorationParams = createDecorationParams(params.context, params.node?.data, value);
  const descriptionText = getBlotterSecurityDescriptionSearchText(decorationParams.security);
  if (descriptionText === '' || existingText.includes(descriptionText)) {
    return '';
  }

  if (!shouldIncludeSecurityDescriptionInSearch(showDescription, decorationParams)) {
    return '';
  }

  return descriptionText;
}

export function wrapBlotterSecurityDescriptionQuickFilter(
  baseGetQuickFilterText: ((params: GetQuickFilterTextParams) => string) | undefined,
  showDescription: AgGridSecurityProps['showDescription']
): (params: GetQuickFilterTextParams) => string {
  const base = baseGetQuickFilterText ?? defaultGetQuickFilterText;

  return params => {
    const baseText = base(params);
    const descriptionText = getSecurityColumnDescriptionQuickFilterText(params, showDescription, baseText);
    if (descriptionText === '') {
      return baseText;
    }

    return baseText === '' ? descriptionText : `${baseText} ${descriptionText}`;
  };
}

export function resolveSecurityColumnRendererParams(
  params: AgGridSecurityProps | undefined
): AgGridSecurityProps | undefined {
  if (params == null) {
    return { showDescription: resolveSecurityColumnShowDescription(undefined) };
  }

  return {
    ...params,
    showDescription: resolveSecurityColumnShowDescription(params),
  };
}
